import { useState } from 'react';
import { createPortal } from 'react-dom';
import { User, LogOut, Settings, Shield, Eye, Users, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types';
import { UserManagement } from './UserManagement';
import { AuditLogViewer } from './AuditLogViewer';
import './UserProfile.css';

interface UserProfileProps {
  onLogout: () => void;
}

export function UserProfile({ onLogout }: UserProfileProps) {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showAuditLogs, setShowAuditLogs] = useState(false);

  if (!user) return null;

  const handleLogout = async () => {
    try {
      await logout();
      onLogout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const getRoleIcon = (role: UserRole) => {
    return role === 'admin' ? Shield : Eye;
  };

  const getRoleColor = (role: UserRole) => {
    return role === 'admin' ? '#e53e3e' : '#3182ce';
  };

  const getRoleLabel = (role: UserRole) => {
    return role === 'admin' ? 'Administrator' : 'Reviewer';
  };

  const RoleIcon = getRoleIcon(user.role);

  return (
    <div className="user-profile">
      <button
        className="user-profile-trigger"
        onClick={() => setIsOpen(!isOpen)}
        title={`${user.displayName || user.email} (${getRoleLabel(user.role)})`}
      >
        <div className="user-avatar">
          <User className="avatar-icon" />
        </div>
        <div className="user-info">
          <span className="user-name">
            {user.displayName || user.email.split('@')[0]}
          </span>
        </div>
      </button>

      {isOpen && (
        <>
          <div 
            className="user-profile-overlay"
            onClick={() => setIsOpen(false)}
          />
          <div className="user-profile-menu">
            <div className="user-profile-header">
              <div className="user-avatar-large">
                <User className="avatar-icon" />
              </div>
              <div className="user-details">
                <h3 className="user-name-large">
                  {user.displayName || 'User'}
                </h3>
                <p className="user-email">{user.email}</p>
                <div 
                  className="user-role-badge"
                  style={{ backgroundColor: getRoleColor(user.role) }}
                >
                  <RoleIcon className="role-icon" />
                  {getRoleLabel(user.role)}
                </div>
              </div>
            </div>

            <div className="user-profile-stats">
              <div className="stat-item">
                <span className="stat-label">Last Login</span>
                <span className="stat-value">
                  {user.lastLogin 
                    ? new Date(user.lastLogin).toLocaleDateString()
                    : 'Unknown'
                  }
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Member Since</span>
                <span className="stat-value">
                  {new Date(user.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="user-profile-actions">
              {user.role === 'admin' && (
                <>
                  <button 
                    className="action-button admin-button"
                    onClick={() => setShowUserManagement(true)}
                  >
                    <Users className="action-icon" />
                    Manage Users
                  </button>
                  <button 
                    className="action-button admin-button"
                    onClick={() => setShowAuditLogs(true)}
                  >
                    <FileText className="action-icon" />
                    Audit Logs
                  </button>
                </>
              )}
              <button className="action-button">
                <Settings className="action-icon" />
                Settings
              </button>
              <button 
                className="action-button logout-button"
                onClick={handleLogout}
              >
                <LogOut className="action-icon" />
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}

      {/* User Management Modal */}
      {showUserManagement && typeof document !== 'undefined' &&
        createPortal(
          <UserManagement
            currentUser={user}
            onClose={() => setShowUserManagement(false)}
          />,
          document.body
        )}

      {/* Audit Log Viewer Modal */}
      {showAuditLogs && typeof document !== 'undefined' &&
        createPortal(
          <AuditLogViewer
            currentUser={user}
            onClose={() => setShowAuditLogs(false)}
          />,
          document.body
        )}
    </div>
  );
}
